import React from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { classes } from './utils';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';

const AddIpAddressInfo = ({ isLoading, setTallyIpInstructionPopup }) => (
  <Card className={classes.tallyCardComponent}>
    <CardHeader
      className={classes.tallyIpCardHeader}
      title={
        <Grid container alignItems="center">
          <Grid item xs={11}>
            <Typography variant="h6">
              Expose a Private IP to a Public IP using Port Forwarding
            </Typography>
          </Grid>
          <Grid item xs={1} align="right">
            <Avatar
              onClick={() => {
                setTallyIpInstructionPopup(false);
              }}
              src={closeIcon}
              className={classes.closeAction}
            />
          </Grid>
        </Grid>
      }
    />
    {isLoading ? (
      <Box className={classes.loaderBox}>
        <CircularProgress />
      </Box>
    ) : (
      <CardContent className={classes.CardContent}>
        <Grid container direction="row" alignItems="center">
          <Grid item xs={12}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Step-by-Step Guide to Expose a Private IP to a Public IP using
                Port Forwarding
              </Typography>

              <Typography variant="h6" gutterBottom>
                Step 1: Find Your Private IP Address
              </Typography>
              <Typography paragraph>
                Your device is assigned a private IP address (e.g., 192.168.x.x
                or 10.x.x.x) within your local network. To find your private IP:
              </Typography>

              <Typography paragraph>
                On Windows:
                <li>
                  Open Command Prompt (Win + R, type cmd, and press Enter).
                </li>
                <li>
                  Type
                  <code>ipconfig</code> and press Enter.
                </li>
                <li>
                  Look for the section under your network adapter (e.g.,
                  &quot;Ethernet adapter&quot; or &quot;Wireless LAN
                  adapter&quot;). Your private IP will be listed under IPv4
                  Address.
                </li>
              </Typography>

              <Typography paragraph>
                On macOS:
                <li>
                  Open Terminal (Applications &gt; Utilities &gt; Terminal).
                </li>
                <li>
                  Type
                  <code>ifconfig</code> and press Enter.
                </li>
                <li>
                  Look for the section that starts with en0 or en1 for wired or
                  wireless connections, respectively. Your private IP will be
                  next to inet.
                </li>
              </Typography>

              <Typography paragraph>
                On Linux:
                <li>Open Terminal.</li>
                <li>
                  Type
                  <code>ifconfig</code> or
                  <code>ip addr</code>.
                </li>
                <li>
                  Look for inet under the network adapter section, and that is
                  your private IP.
                </li>
              </Typography>

              <Typography variant="h6" gutterBottom>
                Step 2: Access Your Router&apos;s Admin Interface
              </Typography>
              <Typography paragraph>
                To set up port forwarding, you need to log in to your
                router&apos;s admin interface:
                <li>Open a web browser (e.g., Chrome, Firefox).</li>
                <li>
                  In the address bar, type the router&apos;s IP address, usually
                  192.168.1.1 or 192.168.0.1.
                </li>
                <li>
                  Press Enter. You should see the login screen for the router.
                </li>
              </Typography>

              <Typography variant="h6" gutterBottom>
                Step 3: Log In to the Router
              </Typography>
              <Typography paragraph>
                Enter the username and password for your router. Default
                credentials can often be admin/admin or admin/password. Check
                your router&apos;s documentation if unsure.
              </Typography>

              <Typography variant="h6" gutterBottom>
                Step 4: Locate Port Forwarding (Virtual Server) Settings
              </Typography>
              <Typography paragraph>
                Once logged in, find the port forwarding section. It may be
                labeled as Port Forwarding, Virtual Server, NAT Forwarding, or
                under Advanced Settings &gt; NAT or Firewall.
              </Typography>

              <Typography variant="h6" gutterBottom>
                Step 5: Create a Port Forwarding Rule
              </Typography>
              <Typography paragraph>
                To forward a specific port to your private IP address (the
                device you want to expose):
                <li>
                  Internal IP: Enter the private IP address of the device found
                  in Step 1 (e.g., 192.168.1.10).
                </li>
                <li>
                  Internal Port: Specify the port number on your device you want
                  to expose (e.g., 80 for HTTP).
                </li>
                <li>
                  External Port: Specify the public port users will connect to
                  (usually the same as the internal port).
                </li>
                <li>
                  Protocol: Select TCP, UDP, or both, depending on your needs.
                </li>
                <li>Save the settings.</li>
              </Typography>

              <Typography variant="h6" gutterBottom>
                Step 6: Test the Port Forwarding
              </Typography>
              <Typography paragraph>
                Once the port forwarding rule is set, find your public IP
                address using a service like WhatIsMyIP, and test the connection
                using the public IP and port.
              </Typography>

              <Typography variant="h6" gutterBottom>
                Step 7: Configure a Static IP (Optional)
              </Typography>
              <Typography paragraph>
                Set a static IP on your device if its private IP changes
                frequently to ensure the port forwarding rule remains valid.
              </Typography>

              <Typography variant="h6" gutterBottom>
                Step 8: Secure Your Server
              </Typography>
              <Typography paragraph>
                Use strong passwords, enable firewalls, and apply encryption to
                secure your server after exposing it to the public.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    )}
  </Card>
);

AddIpAddressInfo.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  setTallyIpInstructionPopup: PropTypes.func.isRequired,
};

export default AddIpAddressInfo;
